"use client";

import { useCallback, useEffect, useRef } from "react";
import styles from "./hero.module.css";

type Props = { onMove(x: number, z: number): void };

export function WalkJoystick({ onMove }: Props) {
  const base = useRef<HTMLButtonElement>(null);
  const thumb = useRef<HTMLSpanElement>(null);
  const pointer = useRef<number | null>(null);
  const move = useRef(onMove);
  useEffect(() => { move.current = onMove; }, [onMove]);

  const reset = useCallback(() => {
    pointer.current = null;
    if (thumb.current) thumb.current.style.transform = "translate(0, 0)";
    if (base.current) base.current.dataset.active = "false";
    move.current(0, 0);
  }, []);

  useEffect(() => {
    const hidden = () => { if (document.hidden) reset(); };
    window.addEventListener("blur", reset);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("blur", reset);
      document.removeEventListener("visibilitychange", hidden);
      reset();
    };
  }, [reset]);

  const update = (clientX: number, clientY: number) => {
    const rect = base.current!.getBoundingClientRect();
    const radius = rect.width * .29;
    const dx = clientX - rect.left - rect.width / 2;
    const dy = clientY - rect.top - rect.height / 2;
    const distance = Math.hypot(dx, dy);
    const amount = Math.min(1, distance / radius);
    const x = distance ? dx / distance : 0;
    const y = distance ? dy / distance : 0;
    if (thumb.current) thumb.current.style.transform = `translate(${x * amount * radius}px, ${y * amount * radius}px)`;
    // A small neutral zone prevents drift when resting a finger in the centre.
    const speed = Math.max(0, (amount - .08) / .92);
    move.current(x * speed || 0, -y * speed || 0);
  };

  return <button ref={base} type="button" className={styles.joystick}
    aria-label="Джойстик движения" aria-describedby="joystick-help" data-active="false"
    onContextMenu={event => event.preventDefault()}
    onPointerDown={event => {
      if (pointer.current !== null || event.button !== 0) return;
      event.preventDefault(); event.stopPropagation();
      pointer.current = event.pointerId;
      if (!document.pointerLockElement) event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.dataset.active = "true";
      update(event.clientX, event.clientY);
    }}
    onPointerMove={event => {
      if (pointer.current !== event.pointerId) return;
      event.preventDefault(); event.stopPropagation(); update(event.clientX, event.clientY);
    }}
    onPointerUp={event => { if (pointer.current === event.pointerId) { event.stopPropagation(); reset(); } }}
    onPointerCancel={event => { if (pointer.current === event.pointerId) reset(); }}
    onLostPointerCapture={event => { if (pointer.current === event.pointerId) reset(); }}
    onBlur={reset}
    onKeyDown={event => {
      const directions: Record<string, [number, number]> = { ArrowUp: [0, 1], ArrowDown: [0, -1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
      if (directions[event.key]) { event.preventDefault(); event.stopPropagation(); move.current(...directions[event.key]); }
    }}
    onKeyUp={event => { if (event.key.startsWith("Arrow")) { event.preventDefault(); event.stopPropagation(); reset(); } }}>
    <span ref={thumb} className={styles.joystickThumb} aria-hidden="true" />
    <span id="joystick-help" className="sr-only">Потяни в нужную сторону. Чем дальше от центра, тем быстрее движение. Отпусти, чтобы остановиться. Можно использовать стрелки клавиатуры.</span>
  </button>;
}
